import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ShieldCheck } from "lucide-react";
import { CallButton } from "./Nav";
import { MaskLines, scrollToId, EASE } from "./motion";
import { HeroFrame } from "./HeroFrame";
import { Wordmark } from "./Logo";

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const headY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const smx = useSpring(mx, { stiffness: 80, damping: 20 });
  const smy = useSpring(my, { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="hero-spot relative overflow-hidden pt-28 lg:pt-32">
      <div className="hero-grid pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-24">
        <motion.div style={{ y: headY }} className="lg:col-span-7 lg:pt-10">
          <motion.div {...fade(0.1)} className="mb-8 inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-400/10 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-brand-300 sm:tracking-[0.2em]" data-testid="hero-badge">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> 30+ yrs · Fully insured · Camp Hill, PA
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1, delay: 0.2, ease: EASE }} className="mb-8" data-testid="hero-company-name">
            <Wordmark className="h-36 sm:h-44 lg:h-52" fg="#EDEDED" knock="#0A0A0A" testId="hero-wordmark" />
          </motion.div>
          <h1 className="sr-only">Complete Maintenance Services, Inc. — commercial and industrial contract cleaning, floor care and general maintenance in Central Pennsylvania</h1>
          <MaskLines
            onLoad
            baseDelay={2}
            aria-hidden="true"
            className="font-display text-[3.4rem] font-black uppercase leading-[0.88] tracking-[-0.04em] text-white sm:text-6xl lg:text-[4.4rem] xl:text-[5rem]"
            lines={["Clean floors.", <span className="text-outline">Clear scopes.</span>, <span className="text-brand-400">Complete.</span>]}
          />
          <motion.p {...fade(0.9)} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="hero-description">
            Commercial &amp; industrial contract cleaning, floor care and general maintenance for facilities across Central Pennsylvania and the surrounding states — from one dependable team that has been doing it for more than three decades.
          </motion.p>
          <motion.div {...fade(1.05)} className="mt-10 flex flex-wrap items-center gap-4">
            <CallButton testId="hero-call-button" className="px-7 py-4 text-base" />
            <button onClick={() => scrollToId("partners")} data-testid="hero-partner-button" className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 font-display text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-brand-400/60 hover:bg-white/5">
              Become a service provider
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5">
          <HeroFrame mx={smx} my={smy} progress={scrollYProgress} />
        </div>
      </div>
    </section>
  );
};
